import {
  CreditSource,
  CreditSurface,
  getCreditClientSettingsByPosition,
  getCreditForPeriodBySurface,
  IAwardBiddingPayload,
  IBidPackageResponse,
  IBidPackageResponseListItem,
  IBidResponse,
  ICreditClientSetting,
  ICreditRolloutMaster,
  ICrewBids,
  IDisplayBidNFA,
  IDisplayBidPairing,
  IDisplayPairing,
  INonFlyingAssignment,
  INonFlyingType,
  IWebsocketBidPkgBuildProgress,
  resolveCreditSource,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { getNFAsInRangeByRange, getPairingsInRangeByRange } from "src/pages/CrewSchedule/TimelineUtils";
import * as BiddingActions from "../actions/Bidding.Actions";

//interface IWebsocketBidPkgBuildProgress
//bidPkgId: item.bidPackageId,
//pairingId: item.pairingId,
//bidPairingId: item._id,
//state: state,
//stateReason: stateReason,
//time: new Date()?.valueOf(),
//pending: stateUpdate.find((x) => x._id === BidPairingState.pending)?.count ?? 0,
//populated: stateUpdate.find((x) => x._id === BidPairingState.populated)?.count ?? 0,
//notApplicable: stateUpdate.find((x) => x._id === BidPairingState.notApplicable)?.count ?? 0,
//error: stateUpdate.find((x) => x._id === BidPairingState.error)?.count ?? 0,

export interface IBiddingState {
  bidPackages: IBidPackageResponseListItem[];
  selectedBidPackage: IBidPackageResponse;
  crewBids: IBidResponse;
  biddingNFACounts: BiddingActions.IBiddingNFACounts;
  assignmentIsDirty: boolean;
  bidPairingCreationProgressMap: {
    [pgkId: string]: IWebsocketBidPkgBuildProgress; // Add progress interface here once it's known.
  };
  awardedBidsVisible: boolean;
}

type IAwardedUnawardedBidItems = Pick<IAwardBiddingPayload, "pairings" | "nfas" | "removePairings" | "removeNFAs">;

const toggleAllBidsInRank = (crewBids: ICrewBids, rank: number, assign?: boolean): ICrewBids => {
  const updatedBidPairings = crewBids?.bids?.[rank]?.displayBidPairings?.map((bp) => ({
    ...bp,
    assigned: !bp.awarded && assign ? assign : false, // Assign all (don't touch items already awarded)
  }));
  const updatedBidNFAs = crewBids?.bids?.[rank]?.displayBidNFAs?.map((bnfa) => ({
    ...bnfa,
    assigned: !bnfa.awarded && assign ? assign : false, // Assign all (don't touch items already awarded)
  }));
  const updatedCrewBids = { ...crewBids };
  updatedCrewBids.bids[rank].displayBidPairings = updatedBidPairings;
  updatedCrewBids.bids[rank].displayBidNFAs = updatedBidNFAs;
  return updatedCrewBids;
};

/**
 * Look through all bids in a crew members package and return all assigned bid items.
 * Create pairings and nfa objects to pass to award function.
 * @param crewBids -
 * @param position - position title used for assigning pairings.
 * @returns
 */
export const returnAllAssignedBids = (crewBids: ICrewBids): IAwardedUnawardedBidItems => {
  const bidItems: IAwardedUnawardedBidItems = {
    //@ts-ignore
    pairings: [],
    //@ts-ignore
    nfas: [],
    //@ts-ignore
    removePairings: [],
    //@ts-ignore
    removeNFAs: [],
  };

  for (const [prefRank, bids] of Object.entries(crewBids?.bids)) {
    const { displayBidPairings, displayBidNFAs } = bids;
    // AWARD / UNAWARD PAIRINGS
    if (displayBidPairings?.length) {
      displayBidPairings?.forEach((bp) => {
        if (!bp.awarded && bp.assigned) {
          const bidPairingAward = {
            pairingId: bp._id,
            bidPairingId: bp.bidPairingId,
            position: bp.positionBidOn,
            prefRank: typeof prefRank === "string" ? parseInt(prefRank) : undefined,
          };
          bidItems.pairings.push(bidPairingAward);
        } else if (bp.awarded && bp.assigned) {
          //!! Figure out how best to handle removal.
          bidItems.removePairings.push(bp?._id);
        }
      });
    }
    // AWARD NFAs
    if (displayBidNFAs?.length) {
      displayBidNFAs?.forEach((bnfa) => {
        if (!bnfa.awarded && bnfa.assigned) {
          const bidNFAAward = {
            time: bnfa.start?.valueOf(),
            assignmentTypeId: bnfa.assignmentType._id,
            prefRank: typeof prefRank === "string" ? parseInt(prefRank) : undefined,
            tier: bnfa.tier,
            _id: bnfa._id,
          };
          bidItems.nfas.push(bidNFAAward);
        } else if (bnfa.awarded && bnfa.assigned) {
          //!! Figure out how best to handle removal.
          bidItems.removeNFAs.push(bnfa?._id);
        }
      });
    }
  }
  return bidItems;
};

/**
 * Function used to find and updated a particular bid pairing from a selected crew member.
 */
const findAndUpdateBidPairing = (updatedBidPairing: IDisplayBidPairing, crewBids: ICrewBids): ICrewBids => {
  const updatedCrewBids: { [key: string]: any } = {};
  for (const [prefRank, bids] of Object.entries(crewBids.bids)) {
    const updatedBidPairings = [...bids.displayBidPairings];
    // When searching for the bid pairing to update, make sure to include the id AND it's assigned pref rank as duplicates are allowed.
    const bpIndex = updatedBidPairings?.findIndex(
      (bp) => bp.bidPairingId === updatedBidPairing.bidPairingId && bp.prefRank === updatedBidPairing.prefRank
    );
    if (bpIndex !== -1) {
      updatedBidPairings[bpIndex] = updatedBidPairing;
    }
    updatedCrewBids[prefRank] = {
      displayBidPairings: updatedBidPairings,
      displayBidNFAs: bids?.displayBidNFAs,
    };
  }
  return { ...crewBids, bids: updatedCrewBids };
};

/**
 * The bid block as calendar-day labels, from the millisecond bounds every caller here already has.
 *
 * Derived in UTC to match how the rest of bidding buckets days (see createBiddingNFACounts). `end` is the
 * exclusive bound the event filters use, so step back a millisecond: a block ending July 1 00:00 has June
 * 30 as its last day.
 */
const bidBlockDays = (start: number, end: number): { startDate: string; endDate: string } => ({
  startDate: moment.utc(start).format("YYYY-MM-DD"),
  endDate: moment.utc(end - 1).format("YYYY-MM-DD"),
});

/** Anything bidding totals credit for: a staged bid item, or an item already on the schedule. */
type IBidBlockItem = IDisplayBidNFA | IDisplayBidPairing | IDisplayPairing | INonFlyingAssignment;

/** The rule the operator-entered totals have always used: an item belongs to the block it STARTS in. */
const startsInBlock = (item: IBidBlockItem, start: number, end: number): boolean =>
  item.start?.valueOf() >= start && item.start?.valueOf() < end;

/** Any overlap at all. Only safe for a record already allocated to its in-block days - see bidBlockCreditFn. */
const overlapsBlock = (item: IBidBlockItem, start: number, end: number): boolean =>
  item.start?.valueOf() < end && item.end?.valueOf() > start;

/**
 * How much credit one item contributes to the bid block, for the bid package's crew grouping. Zero for
 * an item the block does not count.
 *
 * Whether the block counts an item is decided here, per RECORD rather than per grouping, because it
 * depends on what {@link getCreditForPeriodBySurface} resolved the item to:
 *  - an ALLOCATED calculated record has already been clipped to its in-block days, so it counts whenever
 *    it overlaps the block at all - a pairing straddling the boundary contributes only its in-block days;
 *  - anything else carries its whole total: every NFA, every operator-entered value, and a calculated
 *    record that fell back to operator-entered because its module failed. Those count only when they
 *    START in the block, exactly as before. Widening the window for them would count a straddling item
 *    in full in two adjacent blocks.
 */
const bidBlockCreditFn = (
  settings: ICreditClientSetting[],
  bidPackagePos: string,
  crewGroup: string[],
  start: number,
  end: number,
  master?: ICreditRolloutMaster
): ((item: IBidBlockItem) => number) => {
  // No date passed, deliberately: this is the same setting match the operator-entered totals have always
  // used, so an effective-dated setting cannot resolve differently here than in the rest of the UI.
  const setting = getCreditClientSettingsByPosition(bidPackagePos, settings, crewGroup);
  if (!setting) {
    return () => 0;
  }
  const period = bidBlockDays(start, end);
  return (item: IBidBlockItem) => {
    const { credit, allocated } = getCreditForPeriodBySurface(
      item,
      CreditSurface.bidAssignment,
      setting,
      period,
      master
    );
    const counted = allocated ? overlapsBlock(item, start, end) : startsInBlock(item, start, end);
    return counted ? credit : 0;
  };
};

const getDefaultMinCredit = (assignmentType: INonFlyingType): number => {
  return assignmentType.defaultMinCredit && !isNaN(assignmentType.defaultMinCredit)
    ? assignmentType.defaultMinCredit
    : 0;
};
/**
 * Checks all bid pairings and bid nfas of a given crew members and returns true if any are assigned but not awarded
 * @param crewBids -
 * return boolean
 */
export const returnAllStageCredit = (crewBids: ICrewBids, start: number, end: number): number => {
  let credit = 0;
  const keys = Object.keys(crewBids.bids);
  for (const key of keys) {
    const bids = crewBids.bids[key];
    credit += bids?.displayBidNFAs
      ?.filter((nfa) => nfa?.assigned && nfa.start?.valueOf() >= start && nfa.start?.valueOf() < end)
      .reduce(
        (acc, cur) =>
          acc + (cur.awarded ? -getDefaultMinCredit(cur.assignmentType) : getDefaultMinCredit(cur.assignmentType)),
        0
      );
    credit += bids?.displayBidPairings
      ?.filter((p) => p?.assigned && p.start?.valueOf() >= start && p.start?.valueOf() < end)
      .reduce((acc, cur) => acc + (cur.awarded ? -(cur.plannedCredit ?? 0) : (cur.plannedCredit ?? 0)), 0);
  }
  return credit;
};

/**
 * Use this function only if there exists a credit setting
 * @param crewBids
 * @param start
 * @param end
 * @param bidPackagePos - the position on the bid package
 * @param crewGroup - the crew member's crew groups
 * @returns
 */
export const returnAllStagePlannedCreditMap = (
  crewBids: ICrewBids,
  start: number,
  end: number,
  settings: ICreditClientSetting[],
  bidPackagePos: string,
  crewGroup: string[] = [],
  master?: ICreditRolloutMaster
): number => {
  // Whether the block counts an item is decided per record inside getCredit - see bidBlockCreditFn - so
  // this and returnAssignedCredit cannot drift apart on it.
  const getCredit = bidBlockCreditFn(settings, bidPackagePos, crewGroup, start, end, master);

  const calculateCredit = (item: IDisplayBidNFA | IDisplayBidPairing): number => {
    const credit = getCredit(item) ?? 0;
    return item.awarded ? -credit : credit;
  };

  return Object.values(crewBids.bids).reduce((totalCredit, bids) => {
    const nfaCredit =
      bids.displayBidNFAs?.filter((nfa) => nfa?.assigned)?.reduce((acc, nfa) => acc + calculateCredit(nfa), 0) ?? 0;

    const pairingCredit =
      bids.displayBidPairings
        ?.filter((pairing) => pairing?.assigned)
        ?.reduce((acc, pairing) => acc + calculateCredit(pairing), 0) ?? 0;

    return totalCredit + nfaCredit + pairingCredit;
  }, 0);
};

/**
 * Given a particular NFA type, will return an integer (positive on negative) based on the total count of staged/unstaged NFAs of that type
 * i.e If 5 NFA of that type are staged for assigned and 3 are staged for unassignment, total count returned would be 2.
 */
export const returnAllStagedNFATypes = (
  crewBids: ICrewBids,
  start: number,
  end: number,
  nfaTitles: string[]
): number[] => {
  const counts: number[] = [];
  for (const nfaTitle of nfaTitles) {
    const keys = Object.keys(crewBids.bids);
    let staged = 0;
    for (const key of keys) {
      const bids = crewBids.bids[key];
      staged += bids?.displayBidNFAs
        ?.filter(
          (nfa) =>
            nfa?.assignmentType?.title === nfaTitle &&
            nfa?.assigned &&
            nfa.start?.valueOf() >= start &&
            nfa.start?.valueOf() < end
        )
        .reduce((acc, cur) => acc + (cur.awarded ? -1 : +1), 0);
    }
    counts.push(staged);
  }
  return counts;
};

/**
 *
 * @param nfas - all currently assigned NFAs from a specific crew member
 * @param start - start range (filter)
 * @param end - end range (filter)
 * @param nfaTitles - NFA titles (filter)
 * @returns
 */
export const returnAssignedNFATypes = (
  nfas: INonFlyingAssignment[],
  start: number,
  end: number,
  nfaTitles?: string[]
): number[] => {
  const counts: number[] = [];
  const NFAsIndex = getNFAsInRangeByRange(nfas, [start, end]);
  const NFATypesInRange = nfas.slice(NFAsIndex.minIndex, NFAsIndex.maxIndex + 1).filter((nfa) => {
    return nfa.start?.valueOf() >= start && nfa.start?.valueOf() < end;
  });
  nfaTitles.forEach((typeTitle) => {
    const nfaTypeCount = NFATypesInRange.filter((n) => n?.assignmentType?.title === typeTitle);
    counts.push(nfaTypeCount?.length);
  });
  return counts;
};

/**
 * Credit that is ACTUALLY on the schedule for the bid block - it doesn't care whether the items are
 * there because of bid assignment or not.
 *
 * Three sources, in order of precedence:
 *  - the calculated credit for this crew grouping's `bidAssignment` surface, when `settings`/`master`
 *    resolve it that way - matching {@link returnAllStagePlannedCreditMap} on the same surface;
 *  - the operator-entered credit map, when `useAdditionalPlannedCredit` is on. Both read the map through
 *    the same accessor, so the two differ only in which record the surface resolves to;
 *  - otherwise the manual `plannedCredit` / NFA default credit.
 */
export const returnAssignedCredit = (
  pairings: IDisplayPairing[],
  nfas: INonFlyingAssignment[],
  start: number,
  end: number,
  settings: ICreditClientSetting[],
  bidPackagePos: string,
  crewGroup: string[] = [],
  master?: ICreditRolloutMaster,
  useAdditionalPlannedCredit = false
): number => {
  const setting = getCreditClientSettingsByPosition(bidPackagePos, settings, crewGroup);
  const usesCalculatedCredit =
    !!setting &&
    resolveCreditSource(CreditSurface.bidAssignment, setting, master).source !== CreditSource.operatorEntered;
  // The credit map is what the operator-entered `useAdditionalPlannedCreditInBidAssignment` path reads too,
  // so it stays on the map accessor whether or not calculated credit is switched on.
  const usesCreditMap = usesCalculatedCredit || useAdditionalPlannedCredit;
  // Whether the block counts an item is decided per record inside getCredit - see bidBlockCreditFn. The
  // manual path keeps the starts-in-block rule itself, so the two never differ on which items count.
  const getCredit = bidBlockCreditFn(settings, bidPackagePos, crewGroup, start, end, master);
  const manualCredit = (item: IDisplayPairing | INonFlyingAssignment, credit: number): number =>
    startsInBlock(item, start, end) ? credit : 0;

  // A coarse window only - wide enough that a record straddling the block boundary reaches getCredit.
  const NFAsIndex = getNFAsInRangeByRange(nfas, [start, end]);
  const candidateNFAs = nfas.slice(NFAsIndex.minIndex, NFAsIndex.maxIndex + 1);
  const pairingsIndex = getPairingsInRangeByRange(pairings, [start, end]);
  const candidatePairings = pairings.slice(pairingsIndex.minIndex, pairingsIndex.maxIndex + 1);

  const nfaCredit = candidateNFAs.reduce((acc, cur) => {
    const credit = usesCreditMap ? getCredit(cur) : manualCredit(cur, getDefaultMinCredit(cur.assignmentType));
    return acc + (credit && !isNaN(credit) ? credit : 0);
  }, 0);
  const pairingCredit = candidatePairings.reduce((acc, cur) => {
    const credit = usesCreditMap ? getCredit(cur) : manualCredit(cur, cur.plannedCredit);
    return acc + (credit && !isNaN(credit) ? credit : 0);
  }, 0);

  return nfaCredit + pairingCredit;
};

/**
 * Checks all bid pairings and bid nfas of a given crew members and returns true if any are assigned but not awarded
 * @param crewBids -
 * return boolean
 */
export const returnDirtyIfAssigned = (crewBids: ICrewBids): boolean => {
  for (const bids of Object.values(crewBids.bids)) {
    const dirtyNFAs = bids?.displayBidNFAs?.filter((nfa) => nfa?.assigned);
    const dirtyPairings = bids?.displayBidPairings?.filter((p) => p?.assigned);
    if (dirtyNFAs?.length || dirtyPairings?.length) {
      return true;
    }
  }
  return false;
};

/**
 * Reset all assigned bids to false (reset all changes made to a crew members bid assignments)
 * @param crewBids -
 * return crewBids
 */
export const resetAllAssignedBids = (crewBids: ICrewBids): ICrewBids => {
  const updatedCrewBids: { [key: string]: any } = {};
  for (const [prefRank, bids] of Object.entries(crewBids.bids)) {
    const resetNFAs = bids?.displayBidNFAs?.map((nfa) => ({ ...nfa, assigned: false }));
    const resetPairings = bids?.displayBidPairings?.map((p) => ({ ...p, assigned: false }));
    updatedCrewBids[prefRank] = {
      displayBidPairings: resetPairings,
      displayBidNFAs: resetNFAs,
    };
  }
  //console.log({ ...crewBids, bids: updatedCrewBids });
  return { ...crewBids, bids: updatedCrewBids };
};

/**
 * Function used to find and updated a particular bid nfa from a selected crew member.
 */
const findAndUpdateBidNFA = (updatedBidNFA: IDisplayBidNFA, crewBids: ICrewBids): ICrewBids => {
  const updatedCrewBids: { [key: string]: any } = {};
  for (const [prefRank, bids] of Object.entries(crewBids.bids)) {
    const updatedBidNFAs = [...bids.displayBidNFAs];
    // When searching for the bid nfa to update, make sure to include the id AND it's assigned pref rank as duplicates are allowed.
    const bpIndex = updatedBidNFAs?.findIndex(
      (bnfa) => bnfa._id === updatedBidNFA._id && bnfa.prefRank === updatedBidNFA.prefRank
    );
    if (bpIndex !== -1) {
      updatedBidNFAs[bpIndex] = updatedBidNFA;
    }
    updatedCrewBids[prefRank] = {
      displayBidPairings: bids?.displayBidPairings,
      displayBidNFAs: updatedBidNFAs,
    };
  }
  return { ...crewBids, bids: updatedCrewBids };
};

const initialState: IBiddingState = {
  bidPackages: [],
  selectedBidPackage: null,
  crewBids: {},
  biddingNFACounts: null,
  // used to block certain interactivity if assigned but unwarded bids exist in a crew row.
  // This boolean gets reset to fase only when a crew's bid assignments are saved (awarded).
  assignmentIsDirty: false,
  bidPairingCreationProgressMap: {},
  // Awarded Bid Pairing visibility is used in 3 areas.
  // Show/Hide (optional using the boolean below) of the bid pairings is set in line 610 of CrewScheduleGl.ts
  // All ineligible bid pairings are shown greyed out - set in line 84 of PairingsGL.ts
  // All ineligible bid pairing interactivity is turned off - set in line 634 of Crew Schedule Canvas.tsx
  awardedBidsVisible: true,
};

/**
 * If user clinks on an NFA to assign/unassign it, update the assignment count displayed in the NFA assignment bars.
 * @param updatedNFA - nfa user clicked on.
 * @param biddingNFACounts - currently saved nfa counts.
 * @returns
 */
export const updateNFAAssignmentCounts = (
  updatedNFA: IDisplayBidNFA,
  biddingNFACounts: BiddingActions.IBiddingNFACounts
): BiddingActions.IBiddingNFACounts => {
  if (biddingNFACounts && !!Object.values(biddingNFACounts)?.length && updatedNFA) {
    //const NFATypeId = updatedNFA?.assignmentType?._id;
    // use title for key as type id isn't being returned in the crew NFAs in schedule.
    const NFATypeTitle = updatedNFA?.assignmentType?.title;
    const NFAStartDate: string = moment(updatedNFA?.start).utc().format("YYYY/MM/DD");
    // first condition is a pending award and second condition is a saved award. anything would be considered not assigned or unawarded.
    const assignedOrAwarded =
      (updatedNFA?.assigned && !updatedNFA?.awarded) || (!updatedNFA?.assigned && updatedNFA?.awarded);
    if (biddingNFACounts?.[NFAStartDate]?.[NFATypeTitle]) {
      const updatedNFACounts = { ...biddingNFACounts };
      const updatedAssignmentArray = [...updatedNFACounts[NFAStartDate][NFATypeTitle].assignedAwarded];
      const itemIndex = updatedAssignmentArray.findIndex((nfaId) => nfaId === updatedNFA?._id);
      if (itemIndex === -1 && assignedOrAwarded) {
        updatedAssignmentArray.push(updatedNFA?._id); // store assignment id if assigned or awarded
      } else if (itemIndex > -1 && !assignedOrAwarded) {
        updatedAssignmentArray.splice(itemIndex, 1); // remove assignment id if present and not assigned or awarded
      }
      updatedNFACounts[NFAStartDate][NFATypeTitle].assignedAwarded = updatedAssignmentArray;
      updatedNFACounts[NFAStartDate][NFATypeTitle].count = updatedAssignmentArray?.length ?? 0;
      return updatedNFACounts;
    }
  }
  // Else return unchanged NFA counts
  return biddingNFACounts;
};

/**
 * If updating an entire rank worth of NFA assignments, recursively edit each updated NFA and modify the counts.
 * @param updatedNFAs -
 * @param biddingNFACounts -
 * @param index -
 * @returns
 */
export const recursivelyUpdateNFAAssignmentCounts = (
  updatedNFAs: IDisplayBidNFA[],
  biddingNFACounts: BiddingActions.IBiddingNFACounts,
  index: number
): BiddingActions.IBiddingNFACounts => {
  if (biddingNFACounts && !!Object.values(biddingNFACounts)?.length && updatedNFAs?.length && updatedNFAs?.[index]) {
    const updatedNFA = updatedNFAs?.[index];
    const updatedNFACounts = updateNFAAssignmentCounts(updatedNFA, biddingNFACounts);
    if (index < updatedNFAs?.length) {
      recursivelyUpdateNFAAssignmentCounts(updatedNFAs, updatedNFACounts, index + 1);
    }
    return updatedNFACounts;
  }
  // Else return unchanged NFA counts
  return biddingNFACounts;
};

export const BiddingReducer = (state: IBiddingState = initialState, action: BiddingActions.Action): IBiddingState => {
  switch (action.type) {
    case BiddingActions.Type.GOT_BID_PACKAGE_LIST:
      return {
        ...state,
        bidPackages: [...action.bidPackages],
        assignmentIsDirty: false,
      };
    case BiddingActions.Type.SET_BID_PACKAGE:
      return {
        ...state,
        selectedBidPackage: action.bidPackage ?? null,
        assignmentIsDirty: false,
      };
    case BiddingActions.Type.UPDATE_CREW_BID_EVENT: {
      const updatedSelectedCrewBids = action.bidPairing
        ? findAndUpdateBidPairing(action.bidPairing, state.crewBids[action.crewId])
        : findAndUpdateBidNFA(action.bidNFA, state.crewBids[action.crewId]);

      const updatedNFACounts = action.bidNFA
        ? updateNFAAssignmentCounts(action.bidNFA, state.biddingNFACounts)
        : state.biddingNFACounts;

      const updatedCrewBids = { ...state.crewBids, [action.crewId]: updatedSelectedCrewBids };
      return {
        ...state,
        crewBids: updatedCrewBids,
        biddingNFACounts: updatedNFACounts,
        assignmentIsDirty: returnDirtyIfAssigned(updatedSelectedCrewBids),
      };
    }
    case BiddingActions.Type.TOGGLE_ASSIGN_ALL_CREW_BIDS_RANK: {
      const { crewId, rank, assign } = action;
      // console.log(`${assign ? "Assign" : "Unassing"} all bid pairings for ${rank}.`);
      const updatedBids = toggleAllBidsInRank(state.crewBids[crewId], rank, assign);
      const updatedCounts = recursivelyUpdateNFAAssignmentCounts(
        updatedBids?.bids?.[rank]?.displayBidNFAs,
        state.biddingNFACounts,
        0
      );
      const updatedCrewBidAssignments = {
        ...state.crewBids,
        [action.crewId]: updatedBids,
      };
      return {
        ...state,
        crewBids: updatedCrewBidAssignments,
        biddingNFACounts: updatedCounts,
        assignmentIsDirty: returnDirtyIfAssigned(updatedBids),
      };
    }
    case BiddingActions.Type.SET_BID_NFA_COUNTS:
      return {
        ...state,
        biddingNFACounts: action.biddingNFACounts,
      };
    case BiddingActions.Type.GOT_CREWMEMBER_BIDS: {
      const [crewMemberBids] = Object.values(action.crewMemberBids);

      //! IMPORTANT Loop through all ranks, and sort pairings and nfas by start time (required for selection to work properly in the schedule view)
      Object.values(crewMemberBids?.bids)?.forEach((bids) => {
        bids?.displayBidPairings?.sort((a, b) => a.start?.valueOf() - b.start?.valueOf());
        bids?.displayBidNFAs?.sort((a, b) => a.start?.valueOf() - b.start?.valueOf());
      });

      return {
        ...state,
        crewBids: { ...state.crewBids, ...action.crewMemberBids },
        assignmentIsDirty: returnDirtyIfAssigned(crewMemberBids),
      };
    }
    case BiddingActions.Type.RESET_BIDDING_TOOL:
      // If reset (i.e Crew Schedule window unmounted), return initial state of the bidding tool but don't reset packages.
      return { ...initialState, bidPackages: state.bidPackages };
    case BiddingActions.Type.ADD_BID_PAIRING_CREATION_PROGRESS: {
      const packageId = action?.progress?.bidPkgId;
      if (!packageId) {
        return state;
      } else {
        // If available, replace the stored progress object for this packages bid creation status.
        return { ...state, bidPairingCreationProgressMap: { [packageId]: action.progress } };
      }
    }
    case BiddingActions.Type.SET_AWARDED_BIDS_VISIBILITY:
      return { ...state, awardedBidsVisible: action.isVisible };
    default:
      return state;
  }
};
